import { supabase } from '../lib/supabase';

/** Invitation fields needed for acceptance */
export interface InvitationForAcceptance {
  id: string;
  team_id: string;
  role: string;
  position?: string | null;
}

/** User fields needed for invitation acceptance */
export interface UserForAcceptance {
  id: string;
  email?: string | null;
}

/**
 * Accepts a coach invitation: updates status, waits for trigger, and ensures a coach record exists.
 * Uses fallback insert if the database trigger doesn't create the coach record.
 * Callers handle success/error UI and navigation.
 */
export async function acceptCoachInvitation(
  invitation: InvitationForAcceptance,
  user: UserForAcceptance
): Promise<{ success: boolean }> {
  const { error: updateError } = await supabase
    .from('coach_invitations')
    .update({
      status: 'accepted',
      accepted_at: new Date().toISOString(),
    })
    .eq('id', invitation.id);

  if (updateError) {
    console.error('[invitationUtils] Failed to update invitation:', updateError);
    return { success: false };
  }

  await new Promise((resolve) => setTimeout(resolve, 1000));

  let { data: coachRecord, error: coachError } = await supabase
    .from('coaches')
    .select('id')
    .eq('team_id', invitation.team_id)
    .eq('user_id', user.id)
    .single();

  if (coachError || !coachRecord) {
    console.warn('[invitationUtils] Coach record not found, attempting fallback insert');
    const fallback = await createCoachRecordFromInvitation(invitation, user);
    if (!fallback.success) {
      return { success: false };
    }
  }

  return { success: true };
}

/**
 * Creates a coach record as a fallback when the database trigger fails.
 * The RLS policy "Invitees can insert coach record for accepted invitation" allows this.
 */
async function createCoachRecordFromInvitation(
  invitation: InvitationForAcceptance,
  user: UserForAcceptance
): Promise<{ success: boolean }> {
  try {
    const userName = user.email
      ? user.email.split('@')[0] || `Coach_${user.id.slice(0, 8)}`
      : `Coach_${user.id.slice(0, 8)}`;

    const { error } = await supabase.from('coaches').insert({
      team_id: invitation.team_id,
      user_id: user.id,
      name: userName,
      position: invitation.position || 'Assistant Coach',
      role: invitation.role,
      email: user.email || null,
    });

    if (error) {
      console.warn('[invitationUtils] Fallback coach insert failed:', error);
      return { success: false };
    }

    return { success: true };
  } catch (err) {
    console.warn('[invitationUtils] Fallback coach insert error:', err);
    return { success: false };
  }
}
